import type { DraggableAttributes } from '@dnd-kit/core';
import type { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { AnimatePresence, motion } from 'motion/react';
import type { ReactNode } from 'react';
import type {
  NodePath,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import { isGroupNode } from '@/components/tree-dnd/tree-dnd-types';
import {
  TreeDndProvider,
  TreeDndSortableList,
  getChildPaths,
} from '@/components/tree-dnd/tree-dnd-context';
import { TreeDndDroppable } from '@/components/tree-dnd/tree-dnd-droppable';
import { TreeDndSortable } from '@/components/tree-dnd/tree-dnd-sortable';
import { TreeDndBox } from '@/components/tree-dnd/tree-dnd-box';
import { TreeDndSiblingGap } from '@/components/tree-dnd/tree-dnd-sibling-gap';
import { TreeDndDropLine } from '@/components/tree-dnd/tree-dnd-drop-line';
import { TreeDndAddButton } from '@/components/tree-dnd/tree-dnd-add-button';
import { TreeDndAddSheet } from '@/components/tree-dnd/tree-dnd-add-sheet';
import { ConditionRenderer } from '@/components/tree-dnd/tree-dnd-conditions/condition-renderer';
import { TreeDndDeleteButton } from '@/components/tree-dnd/tree-dnd-delete-button';
import { TreeDndGroupOperatorToggle } from '@/components/tree-dnd/tree-dnd-group-operator-toggle';
import { TreeDndMoveMenu } from '@/components/tree-dnd/tree-dnd-move-menu';
import { TreeDndNegateButton } from '@/components/tree-dnd/tree-dnd-negate-button';
import { TreeDndNotLabel } from '@/components/tree-dnd/tree-dnd-not-label';
import {
  TreeDndOperatorRail,
  TreeDndOperatorRailBranch,
  TreeDndOperatorRailSpine,
} from '@/components/tree-dnd/tree-dnd-operator-rail';
import { useTreeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import { useTreeDndUiStore } from '@/components/tree-dnd/tree-dnd-ui-store';
import { cn } from '@/lib/utils';

interface TreeDndNodeProps {
  node: TreeNode | TreeGroupNode;
  path: NodePath;
  /** How many nodes share this node's parent (feeds the "Mover" menu). */
  siblingCount?: number;
}

type DragHandleProps = {
  listeners?: SyntheticListenerMap;
  attributes?: DraggableAttributes;
};

function ChildRow({
  negated,
  children,
}: {
  negated: boolean;
  children: ReactNode;
}) {
  return (
    <div className={cn('flex items-start', negated && 'gap-1.5')}>
      <div
        className={cn(
          'flex shrink-0 items-center pt-1',
          !negated && 'pointer-events-none w-0 overflow-hidden opacity-0'
        )}
        aria-hidden={!negated}
      >
        <TreeDndNotLabel />
      </div>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}

function NodeHeaderActions({
  path,
  siblingCount,
  negated,
  showNegate = true,
  children,
}: {
  path: NodePath;
  siblingCount: number;
  negated: boolean;
  showNegate?: boolean;
  children?: ReactNode;
}) {
  const isApplyingAiChanges = useTreeDndStore(
    (state) => state.isApplyingAiChanges
  );

  return (
    <div
      className={cn(
        'ml-auto flex shrink-0 items-center gap-0.5',
        isApplyingAiChanges && 'pointer-events-none opacity-50'
      )}
    >
      {children}
      <TreeDndMoveMenu path={path} siblingCount={siblingCount} />
      {showNegate ? (
        <TreeDndNegateButton path={path} negated={negated} />
      ) : null}
      <TreeDndDeleteButton path={path} />
    </div>
  );
}

function branchPosition(
  index: number,
  count: number
): 'first' | 'middle' | 'last' {
  if (index === 0) return 'first';
  if (index === count - 1) return 'last';
  return 'middle';
}

function GroupChildren({
  node,
  path,
}: {
  node: TreeGroupNode;
  path: NodePath;
}) {
  const childPaths = getChildPaths(path, node.children.length);
  const isApplyingAiChanges = useTreeDndStore(
    (state) => state.isApplyingAiChanges
  );
  const showOperatorRail = node.children.length > 1;

  const childList = (
    <AnimatePresence initial={false} mode="popLayout">
      {node.children.map((child, index) => {
        const childNode = (
          <ChildRow negated={child.negated}>
            <TreeDndNode
              node={child}
              path={[...path, index]}
              siblingCount={node.children.length}
            />
          </ChildRow>
        );

        return (
          <motion.div
            key={child.id}
            layout={!isApplyingAiChanges}
            initial={{ opacity: 0, y: -8, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -4, height: 0 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className="overflow-hidden"
          >
            {showOperatorRail ? (
              <TreeDndOperatorRailBranch
                position={branchPosition(index, node.children.length)}
              >
                {childNode}
              </TreeDndOperatorRailBranch>
            ) : (
              childNode
            )}
            {index < node.children.length - 1 ? (
              showOperatorRail ? (
                <div className="flex items-stretch">
                  <TreeDndOperatorRailSpine className="self-stretch" />
                  <div className="min-w-0 flex-1">
                    <TreeDndSiblingGap parentPath={path} index={index + 1} />
                  </div>
                </div>
              ) : (
                <TreeDndSiblingGap parentPath={path} index={index + 1} />
              )
            ) : null}
          </motion.div>
        );
      })}
    </AnimatePresence>
  );

  return (
    <TreeDndSortableList paths={childPaths}>
      <div className="flex min-h-12 flex-col">
        <TreeDndDropLine parentPath={path} index={0} />
        {showOperatorRail ? (
          <TreeDndOperatorRail operator={node.operator}>
            {childList}
          </TreeDndOperatorRail>
        ) : (
          childList
        )}
        {node.children.length > 0 ? (
          <TreeDndDropLine parentPath={path} index={node.children.length} />
        ) : null}
        <TreeDndAddButton parentPath={path} />
      </div>
    </TreeDndSortableList>
  );
}

function GroupBox({
  node,
  path,
  siblingCount,
  dragHandleProps,
  showHandle,
}: {
  node: TreeGroupNode;
  path: NodePath;
  siblingCount: number;
  dragHandleProps?: DragHandleProps;
  showHandle: boolean;
}) {
  const highlightedNodeId = useTreeDndStore((state) => state.highlightedNodeId);
  const isApplyingAiChanges = useTreeDndStore(
    (state) => state.isApplyingAiChanges
  );

  return (
    <TreeDndDroppable path={path} disabled={isApplyingAiChanges}>
      <TreeDndBox
        nodeType="group"
        showHandle={showHandle}
        dragHandleProps={dragHandleProps}
        highlighted={highlightedNodeId === node.id}
        headerExtra={
          <NodeHeaderActions
            path={path}
            siblingCount={siblingCount}
            negated={node.negated}
          >
            <TreeDndGroupOperatorToggle path={path} operator={node.operator} />
          </NodeHeaderActions>
        }
      >
        <GroupChildren node={node} path={path} />
      </TreeDndBox>
    </TreeDndDroppable>
  );
}

export function TreeDndNode({
  node,
  path,
  siblingCount = 1,
}: TreeDndNodeProps) {
  const isRoot = path.length === 0;
  const isApplyingAiChanges = useTreeDndStore(
    (state) => state.isApplyingAiChanges
  );
  const highlightedNodeId = useTreeDndStore((state) => state.highlightedNodeId);
  const requestConditionEditor = useTreeDndUiStore(
    (state) => state.requestConditionEditor
  );

  if (isRoot && isGroupNode(node)) {
    return (
      <TreeDndDroppable path={path} disabled={isApplyingAiChanges}>
        <GroupChildren node={node} path={path} />
      </TreeDndDroppable>
    );
  }

  if (!isGroupNode(node)) {
    return (
      <TreeDndSortable path={path} disabled={isApplyingAiChanges}>
        {({ dragHandleProps }) => (
          <TreeDndBox
            nodeType="condition"
            dragHandleProps={dragHandleProps}
            highlighted={highlightedNodeId === node.id}
            onActivate={() => requestConditionEditor(node.id)}
            headerExtra={
              <>
                <ConditionRenderer path={path} node={node} />
                <NodeHeaderActions
                  path={path}
                  siblingCount={siblingCount}
                  negated={node.negated}
                  showNegate={false}
                />
              </>
            }
          />
        )}
      </TreeDndSortable>
    );
  }

  return (
    <TreeDndSortable path={path} disabled={isApplyingAiChanges}>
      {({ dragHandleProps }) => (
        <GroupBox
          node={node}
          path={path}
          siblingCount={siblingCount}
          showHandle
          dragHandleProps={dragHandleProps}
        />
      )}
    </TreeDndSortable>
  );
}

export function TreeDndTree({ emptyFallback }: { emptyFallback?: ReactNode }) {
  const tree = useTreeDndStore((state) => state.tree);
  const addSheetParentPath = useTreeDndUiStore(
    (state) => state.addSheetParentPath
  );
  const clearAddSheet = useTreeDndUiStore((state) => state.clearAddSheet);
  const isEmpty = tree.children.length === 0;

  return (
    <TreeDndProvider>
      {isEmpty && emptyFallback ? (
        emptyFallback
      ) : (
        <TreeDndNode node={tree} path={[]} />
      )}
      <TreeDndAddSheet
        open={addSheetParentPath !== null}
        onOpenChange={(open) => {
          if (!open) clearAddSheet();
        }}
        parentPath={addSheetParentPath ?? []}
      />
    </TreeDndProvider>
  );
}
